import React from 'react';
import { Fragment, useState } from 'react';
import { Form, redirect, useNavigation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-toastify';
import { XMarkIcon } from '@heroicons/react/24/outline';
import customFetch from '@utils/customFetch';
import { LINK_TYPE } from '@utils/constants';
import { useLinksContext } from '@pages/Links';
import LinkEditSlideInLinkType from './LinkEditSlideInLinkType';
import IconSelector from '@utils/icons/IconSelector';

export const action =
  (queryClient) =>
  async ({ request }) => {
    const formData = await request.formData();
    const data = Object.fromEntries(formData);
    const itemId = data._id;
    const targetUserId = data.targetUserId;
    const queryParam = targetUserId ? `?targetUserId=${targetUserId}` : '';
    delete data._id;
    delete data.targetUserId;

    if (data.new === 'true') {
      try {
        await customFetch.post(`/links${queryParam}`, data);
        queryClient.invalidateQueries(['links']);
        if (targetUserId) queryClient.invalidateQueries(['userLinks', targetUserId]);
        toast.success('Erfolgreich erstellt!');
        return null;
      } catch (error) {
        toast.error(error?.response?.data?.msg);
        return error;
      }
    } else {
      try {
        await customFetch.patch(`/links/${itemId}${queryParam}`, data);
        queryClient.invalidateQueries(['links']);
        if (targetUserId) queryClient.invalidateQueries(['userLinks', targetUserId]);
        toast.success('Erfolgreich angepasst!');
        return null;
      } catch (error) {
        toast.error(error?.response?.data?.msg);
        return error;
      }
    }
  };

const LinkEditSlideIn = () => {
  const {
    showSlideIn,
    setShowSlideIn,
    linkEdit,
    editIconValue,
    setEditIconValue,
    setLinkEdit,
    targetUserId,
  } = useLinksContext();

  const [isIconSelectorOpen, setIsIconSelectorOpen] = useState(false);

  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  const handleTypeChange = (event) => {
    setLinkEdit({
      ...linkEdit,
      type: event.target.value,
    });
  };

  return (
    <Transition.Root show={showSlideIn} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setShowSlideIn}>
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <Form
                    method="POST"
                    className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
                    acceptCharset="UTF-8"
                    encType="application/x-www-form-urlencoded"
                  >
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
                      <div className="px-4 sm:px-6">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                            Link bearbeiten
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500"
                              onClick={() => {
                                setShowSlideIn(false);
                                setIsIconSelectorOpen(false);
                              }}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="relative mt-6 flex-1 px-4 sm:px-6">
                        {/* Link-Bearbeitungsformular Start */}
                        <div className="flex h-full flex-col">
                          <div className="space-y-6 pb-5 pt-6">
                            <div>
                              <label
                                htmlFor="title"
                                className="block text-sm font-medium leading-6 text-gray-900"
                              >
                                Titel
                              </label>
                              <div className="mt-2">
                                <input
                                  type="text"
                                  name="title"
                                  id="title"
                                  defaultValue={linkEdit.title}
                                  className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="pb-5">
                            <label
                              htmlFor="type"
                              className="block text-sm font-medium leading-6 text-gray-900"
                            >
                              Link-Typ
                            </label>
                            <div className="mt-2">
                              <select
                                id="type"
                                name="type"
                                className="mt-2 block w-full rounded-md border-0 px-3 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                                value={linkEdit.type}
                                onChange={handleTypeChange}
                              >
                                {Object.values(LINK_TYPE).map((linkType) => {
                                  return (
                                    <option key={linkType} value={linkType}>
                                      {linkType}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          </div>

                          <LinkEditSlideInLinkType
                            type={linkEdit.type}
                            defaultLink={linkEdit}
                          />

                          <div className="pb-5">
                            <div className="col-span-full">
                              <label
                                htmlFor="icon"
                                className="block text-sm font-medium leading-6 text-gray-900"
                              >
                                Icon
                              </label>
                              <input
                                type="hidden"
                                id="icon"
                                name="icon"
                                value={editIconValue || ''}
                              ></input>
                              <div className="mt-2 gap-x-3">
                                <IconSelector
                                  value={editIconValue}
                                  onChange={(v) => setEditIconValue(v)} // Update the selected icon when changed
                                  isOpen={isIconSelectorOpen}
                                  onOpen={() => setIsIconSelectorOpen(true)} // Open the icon selector
                                  onClose={() => setIsIconSelectorOpen(false)} // Close the icon selector
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <input
                          type="hidden"
                          id="new"
                          name="new"
                          value={linkEdit.new}
                        ></input>
                        <input
                          type="hidden"
                          id="_id"
                          name="_id"
                          value={linkEdit._id}
                        ></input>
                        <input
                          type="hidden"
                          id="position"
                          name="position"
                          value={linkEdit.position}
                        ></input>
                        {targetUserId && (
                          <input
                            type="hidden"
                            name="targetUserId"
                            value={targetUserId}
                          />
                        )}
                        {/* Link-Bearbeitungsformular Ende */}
                      </div>
                    </div>
                    <div className="flex shrink-0 justify-end px-4 py-4">
                      <button
                        type="button"
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
                        onClick={() => {
                          setShowSlideIn(false);
                          setIsIconSelectorOpen(false);
                        }}
                      >
                        Abbrechen
                      </button>
                      <button
                        type="submit"
                        className="ml-4 inline-flex justify-center rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500"
                        onClick={() => {
                          setShowSlideIn(false);
                          setIsIconSelectorOpen(false);
                        }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Speichert...' : 'Speichern'}
                      </button>
                    </div>
                  </Form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default LinkEditSlideIn;
